import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import useI18n from "../hooks/useI18n";
import locales from "../locales.json";

const BRANDS = [
    "Mercedes-Benz","Volkswagen","Ford","Renault","Peugeot","Citroën",
    "Fiat","Opel","Vauxhall","Iveco","MAN","Toyota","Nissan"
];

export default function SettingsSheet({ open, onClose, user }) {
    const { t } = useI18n(locales);
    const [username, setUsername] = useState(user?.username || "");
    const [markerColor, setMarkerColor] = useState("#34d399");
    const [vanBrand, setVanBrand] = useState("");

    useEffect(() => {
        if (!open) return;
        setUsername(user?.username || "");
        try { const c = localStorage.getItem("markerColor"); if (c) setMarkerColor(c); } catch {}
        try { const b = localStorage.getItem("vanBrand"); if (b) setVanBrand(b); } catch {}
    }, [open, user]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const validName = username.trim().length >= 3 && username.trim().length <= 24;

    const save = () => {
        const u = { ...(user || {}), username: username.trim() };
        try { localStorage.setItem("dk_user", JSON.stringify(u)); } catch {}
        try {
            localStorage.setItem("markerColor", markerColor);
            window.dispatchEvent(new CustomEvent("markerColor:update", { detail: markerColor }));
        } catch {}
        try {
            if (vanBrand) localStorage.setItem("vanBrand", vanBrand);
            else localStorage.removeItem("vanBrand");
        } catch {}
        window.location.reload();
    };

    const ui = (
        <div className="fixed inset-0 z-[99999]">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
            <div
                className="absolute right-0 top-0 h-full w-full max-w-md bg-slate-900 border-l border-white/10 shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-label={t("mobile.settings")}
            >
                <div
                    className="sticky top-0 z-10 bg-slate-900 px-5 py-4 border-b border-white/10 flex items-center justify-between"
                    style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
                >
                    <h2 className="text-lg font-semibold">{t("mobile.settings")}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label={t("common.close")}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-6">
                    <div>
                        <label className="block text-sm text-white/70 mb-1">{t("settings.username")}</label>
                        <input
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            maxLength={24}
                            className="w-full rounded-xl bg-slate-900 border border-white/10 px-4 py-2 outline-none focus:border-emerald-400"
                            placeholder={t("settings.username_ph")}
                        />
                        <div className="mt-1 text-xs text-white/50">{t("settings.username_rule")}</div>
                    </div>

                    <div>
                        <label className="block text-sm text-white/70 mb-1">{t("settings.marker_color")}</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                value={markerColor}
                                onChange={(e) => setMarkerColor(e.target.value)}
                                className="h-10 w-12 rounded border border-white/10 bg-slate-900 p-0"
                            />
                            <input
                                value={markerColor}
                                onChange={(e) => setMarkerColor(e.target.value)}
                                className="flex-1 rounded-xl bg-slate-900 border border-white/10 px-4 py-2 outline-none focus:border-emerald-400"
                                placeholder="#34d399"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm text-white/70 mb-1">{t("settings.van_brand")}</label>
                        <select
                            value={vanBrand}
                            onChange={(e) => setVanBrand(e.target.value)}
                            className="w-full rounded-xl bg-slate-900 border border-white/10 px-4 py-2 outline-none focus:border-emerald-400"
                        >
                            <option value="">{t("settings.not_selected")}</option>
                            {BRANDS.map((b) => (<option key={b} value={b}>{b}</option>))}
                        </select>
                    </div>

                    <div className="pt-2 flex items-center justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10"
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            onClick={save}
                            disabled={!validName}
                            className={`px-4 py-2 rounded-xl border font-medium ${
                                validName
                                    ? "bg-emerald-500/90 hover:bg-emerald-500 border-emerald-400"
                                    : "bg-emerald-500/40 border-white/10 cursor-not-allowed"
                            }`}
                        >
                            {t("settings.save_restart")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(ui, document.body);
}
